import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import { setAuthTokenGetter, setLogoutHandler } from '../services/auth';

const UserContext = createContext(null);

// Role constants for consistency
export const ROLES = {
  SUPER_ADMIN: 'super_admin',
  ADMIN: 'admin',
  AGENT: 'agent',
};

// Permission constants matching server-side definitions
export const PERMISSIONS = {
  // Organizations
  ORGS_VIEW_ALL: 'organizations.view_all',
  ORGS_VIEW_OWN: 'organizations.view_own',
  ORGS_CREATE: 'organizations.create',
  ORGS_UPDATE_ALL: 'organizations.update_all',
  ORGS_UPDATE_OWN: 'organizations.update_own',
  ORGS_DELETE: 'organizations.delete',

  // Locations
  LOCATIONS_VIEW_ALL: 'locations.view_all',
  LOCATIONS_VIEW_ORG: 'locations.view_org',
  LOCATIONS_VIEW_ASSIGNED: 'locations.view_assigned',
  LOCATIONS_CREATE: 'locations.create',
  LOCATIONS_UPDATE: 'locations.update',
  LOCATIONS_DELETE: 'locations.delete',

  // Users
  USERS_VIEW_ALL: 'users.view_all',
  USERS_VIEW_ORG: 'users.view_org',
  USERS_INVITE: 'users.invite',
  USERS_UPDATE: 'users.update',
  USERS_DELETE: 'users.delete',
  USERS_ASSIGN_ROLES: 'users.assign_roles',
  USERS_DEACTIVATE: 'users.deactivate',
  USERS_TRANSFER: 'users.transfer',

  // Products
  PRODUCTS_VIEW: 'products.view',
  PRODUCTS_CREATE: 'products.create',
  PRODUCTS_UPDATE: 'products.update',
  PRODUCTS_DELETE: 'products.delete',

  // Creatives/Flyers
  CREATIVES_VIEW: 'creatives.view',
  CREATIVES_CREATE: 'creatives.create',
  CREATIVES_UPDATE: 'creatives.update',
  CREATIVES_DELETE: 'creatives.delete',

  // Templates
  TEMPLATES_VIEW: 'templates.view',
  TEMPLATES_CREATE: 'templates.create',
  TEMPLATES_UPDATE: 'templates.update',
  TEMPLATES_DELETE: 'templates.delete',
};

export const UserProvider = ({ children }) => {
  const { isAuthenticated, getAccessTokenSilently, logout, user } = useAuth0();

  // Initialize the auth token getter and logout handler for apiClient SYNCHRONOUSLY
  // This ensures they are available immediately when components mount
  setAuthTokenGetter(getAccessTokenSilently);
  setLogoutHandler(logout);

  const [userInfo, setUserInfo] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [accessibleLocations, setAccessibleLocations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchUserPermissions = useCallback(async () => {
    if (!isAuthenticated || !user?.sub) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const token = await getAccessTokenSilently();
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER_DOMAIN}/api/users/me/permissions`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setUserInfo({
        id: response.data.user_id,
        email: response.data.email,
        role: response.data.role,
        status: response.data.status || 'active',
        organizationId: response.data.organization_id
      });
      setPermissions(response.data.permissions || []);
      setAccessibleLocations(response.data.accessible_location_ids || []);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch user permissions:', err);
      setError(err.message);
      // Set default values for new users who don't have a role yet
      setUserInfo({
        id: user.sub,
        email: user.email,
        role: ROLES.AGENT,
        status: 'active',
        organizationId: null
      });
      setPermissions([]);
      setAccessibleLocations([]);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, getAccessTokenSilently, user?.sub, user?.email]);

  useEffect(() => {
    fetchUserPermissions();
  }, [fetchUserPermissions]);

  // Check if user has a specific role
  const hasRole = useCallback((roles) => {
    if (!userInfo?.role) return false;
    const roleArray = Array.isArray(roles) ? roles : [roles];
    return roleArray.includes(userInfo.role);
  }, [userInfo?.role]);

  // Check if user has a specific permission
  const hasPermission = useCallback((permissionName) => {
    if (!permissions.length) return false;
    if (Array.isArray(permissionName)) {
      return permissionName.some(p => permissions.includes(p));
    }
    return permissions.includes(permissionName);
  }, [permissions]);

  // Check if user can access a specific location
  const canAccessLocation = useCallback((locationId) => {
    if (!userInfo) return false;
    // Super admin and admin can access all locations in their org
    if (userInfo.role === ROLES.SUPER_ADMIN || userInfo.role === ROLES.ADMIN) {
      return true;
    }
    // Agents can only access assigned locations
    const locId = typeof locationId === 'string' ? locationId : locationId?.toString();
    return accessibleLocations.some(id => id?.toString() === locId);
  }, [userInfo, accessibleLocations]);

  // Check if user is an admin (admin or super_admin)
  const isAdmin = useCallback(() => {
    return hasRole([ROLES.ADMIN, ROLES.SUPER_ADMIN]);
  }, [hasRole]);

  // Check if user is a super admin
  const isSuperAdmin = useCallback(() => {
    return hasRole(ROLES.SUPER_ADMIN);
  }, [hasRole]);

  // Check if user is an agent
  const isAgent = useCallback(() => {
    return hasRole(ROLES.AGENT);
  }, [hasRole]);

  // Check if user can manage users (invite, edit roles, etc.)
  const canManageUsers = useCallback(() => {
    return hasPermission([PERMISSIONS.USERS_INVITE, PERMISSIONS.USERS_ASSIGN_ROLES]);
  }, [hasPermission]);

  // Check if user can manage locations
  const canManageLocations = useCallback(() => {
    return hasPermission([PERMISSIONS.LOCATIONS_CREATE, PERMISSIONS.LOCATIONS_UPDATE]);
  }, [hasPermission]);

  // Check if user can manage products (admins/super_admins always can)
  const canManageProducts = useCallback(() => {
    if (userInfo?.role === ROLES.SUPER_ADMIN || userInfo?.role === ROLES.ADMIN) return true;
    return hasPermission([PERMISSIONS.PRODUCTS_VIEW, PERMISSIONS.PRODUCTS_CREATE]);
  }, [hasPermission, userInfo?.role]);

  // Check if user can manage creatives/flyers
  const canManageCreatives = useCallback(() => {
    return hasPermission([PERMISSIONS.CREATIVES_VIEW, PERMISSIONS.CREATIVES_CREATE]);
  }, [hasPermission]);

  // Refresh user data (call after role/permission changes)
  const refreshUserData = useCallback(() => {
    return fetchUserPermissions();
  }, [fetchUserPermissions]);

  const value = {
    userInfo,
    permissions,
    accessibleLocations,
    isLoading,
    error,
    hasRole,
    hasPermission,
    canAccessLocation,
    isAdmin,
    isSuperAdmin,
    isAgent,
    canManageUsers,
    canManageLocations,
    canManageProducts,
    canManageCreatives,
    refreshUserData,
    // Export constants for components to use
    ROLES,
    PERMISSIONS,
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

export default UserContext;
